// src/colorLinks.mjs
// Pure helpers for the per-set "Link colours" feature (variation photos, part 1).
// A set (profile) may carry a `colorLinks` object binding a variation template's
// colour-property options to mockups IN that set. Publish consumes sets, so the
// link rides the first pipeline chunk alongside the set/profile id.
//
// Stored shape (lives on the profile, next to templateIds/sortMap):
//   colorLinks: {
//     templateId,        // the chosen variation template's id
//     templateName,      // its name at link time (for display if it later 404s)
//     propertyKey,       // which property is the colour axis (its property_name)
//     map: { [optionValue]: <mockup templateId in this set> },
//     savedAt,           // ms epoch, passed in by the caller (kept pure here)
//   }
//
// Every function here is pure (no Date.now, no I/O) so it unit-tests cleanly and
// imports safely into both the CRA bundle and `node --test`.

const COLOR_RE = /colou?r/i;

// Normalize a raw template-list response into the minimal shape the studio uses.
// Defensive: tolerates a non-object, a missing/!array templates field, and any
// malformed entry. Mirrors the edge-proxy allowlist so the client never trusts
// unshaped input. Zero templates → []. Never throws.
export function parseTemplatesResponse(json) {
  const src = json && Array.isArray(json.templates) ? json.templates : [];
  const out = [];
  for (const t of src) {
    if (!t || typeof t !== "object" || t.id == null) continue;
    const props = Array.isArray(t.property_names) ? t.property_names : [];
    out.push({
      id: String(t.id),
      name: t.name == null ? "" : String(t.name),
      property_names: props
        .filter((p) => p && typeof p === "object")
        .map((p) => ({
          property_id: p.property_id ?? null,
          property_name: p.property_name == null ? "" : String(p.property_name),
          options: Array.isArray(p.options) ? p.options.map((o) => String(o)) : [],
        })),
    });
  }
  return out;
}

// The default colour property to preselect: the property whose name contains
// "color"/"colour" — but ONLY when exactly one matches. Zero matches or an
// ambiguous multi-match returns null so the UI asks the member to pick instead
// of silently guessing wrong.
export function detectColorPropertyKey(propertyNames) {
  const props = Array.isArray(propertyNames) ? propertyNames : [];
  const matches = props.filter((p) => p && COLOR_RE.test(String(p.property_name || "")));
  return matches.length === 1 ? String(matches[0].property_name) : null;
}

// Look up a property by its property_name key on a template. null if absent.
export function propertyByKey(template, propertyKey) {
  const props = (template && Array.isArray(template.property_names)) ? template.property_names : [];
  return props.find((p) => p && String(p.property_name) === String(propertyKey)) || null;
}

// The option values of the chosen colour property (or [] if the property/template
// is gone). Deduped, string-coerced, order preserved.
export function optionsForProperty(template, propertyKey) {
  const prop = propertyByKey(template, propertyKey);
  const opts = prop && Array.isArray(prop.options) ? prop.options : [];
  const seen = new Set();
  const out = [];
  for (const o of opts) {
    const v = String(o);
    if (!seen.has(v)) { seen.add(v); out.push(v); }
  }
  return out;
}

// Build the stored colorLinks object. `map` is filtered to real assignments only
// (a non-empty mockup id). savedAt is supplied by the caller (kept pure).
export function makeColorLinks({ template, propertyKey, map, savedAt }) {
  const cleanMap = {};
  const src = map && typeof map === "object" ? map : {};
  for (const k of Object.keys(src)) {
    const v = src[k];
    if (v != null && v !== "") cleanMap[k] = String(v);
  }
  return {
    templateId: template && template.id != null ? String(template.id) : null,
    templateName: template && template.name != null ? String(template.name) : "",
    propertyKey: propertyKey == null ? null : String(propertyKey),
    map: cleanMap,
    savedAt: savedAt ?? null,
  };
}

// Remove any map entry whose target mockup id is no longer a valid slot in the
// set (deleted mockup / removed from set). Returns a NEW colorLinks (never
// mutates); returns the input unchanged if nothing was pruned; returns null for
// a null input. The link object survives even if its map empties — the member
// chose a template; only dangling option→mockup refs are dropped.
export function pruneColorLinks(colorLinks, validMockupIds) {
  if (!colorLinks || typeof colorLinks !== "object") return colorLinks || null;
  const valid = validMockupIds instanceof Set ? validMockupIds : new Set(validMockupIds || []);
  const src = colorLinks.map && typeof colorLinks.map === "object" ? colorLinks.map : {};
  const nextMap = {};
  let changed = false;
  for (const k of Object.keys(src)) {
    if (valid.has(String(src[k]))) nextMap[k] = String(src[k]);
    else changed = true;
  }
  return changed ? { ...colorLinks, map: nextMap } : colorLinks;
}

// True when colorLinks references a template that is no longer among the fetched
// templates (the "stale template" case). The UI keeps the saved link and prompts
// a re-pick rather than dropping it.
export function isTemplateStale(colorLinks, availableTemplateIds) {
  if (!colorLinks || colorLinks.templateId == null) return false;
  const ids = availableTemplateIds instanceof Set
    ? availableTemplateIds
    : new Set((availableTemplateIds || []).map(String));
  return !ids.has(String(colorLinks.templateId));
}

// Does this link actually bind at least one option to a mockup? Used to decide
// whether to attach it to the pipeline payload and how to badge the set.
export function hasAssignments(colorLinks) {
  return !!(colorLinks && colorLinks.map && Object.keys(colorLinks.map).length > 0);
}

// Build the rows for the "Link photos to variations" set picker — step 0 of the
// modal when it is opened WITHOUT a specific set. Pure: the caller supplies a
// mockup counter so this stays free of the studio's `saved` state. Each row
// carries the display name, mockup count, video flag, and the colour-link badge
// (linked + how many options are bound). Order is preserved; a non-array `sets`
// yields []. Never throws.
export function buildSetPickerRows(sets, countForSet) {
  const list = Array.isArray(sets) ? sets : [];
  const count = typeof countForSet === "function" ? countForSet : () => 0;
  return list.map((s) => {
    const linked = hasAssignments(s && s.colorLinks);
    const n = Number(count(s));
    return {
      id: s && s.id != null ? s.id : null,
      name: s && s.name ? String(s.name) : "",
      mockupCount: Number.isFinite(n) && n > 0 ? Math.floor(n) : 0,
      hasVideo: !!(s && s.videoId),
      linked,
      linkedCount: linked ? Object.keys(s.colorLinks.map).length : 0,
    };
  });
}

// The value to send on the FIRST pipeline chunk: prune to the mockups actually
// in the payload (no dangling refs cross the wire), and only if something remains
// bound. null → attach nothing.
export function colorLinksForPayload(colorLinks, sentMockupIds) {
  if (!colorLinks) return null;
  const pruned = pruneColorLinks(colorLinks, sentMockupIds);
  return hasAssignments(pruned) ? pruned : null;
}
